import React, { useMemo } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { Building2, FileText, Receipt, Settings, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

function B2BUserDashboard() {
  const navigate = useNavigate();
  const brand = useMemo(
    () => ({
      bg: "#F7FAFC",
      text: "#0F172A",
      subtext: "rgba(15, 23, 42, 0.72)",
      stroke: "rgba(2, 6, 23, 0.10)",
      accent: "#0EA5E9",
      accent2: "#22C55E",
    }),
    []
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: brand.bg, py: 5 }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            border: `1px solid ${brand.stroke}`,
            backgroundImage:
              'linear-gradient(90deg, rgba(14,165,233,0.12), rgba(34,197,94,0.10)), url("https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1400&h=380&fit=crop")',
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <Box sx={{ p: { xs: 3, md: 4 } }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} alignItems={{ xs: "flex-start", sm: "center" }}>
              <Avatar
                src="https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=200&h=200&fit=crop&crop=face"
                sx={{ width: 72, height: 72, border: "3px solid rgba(255,255,255,0.9)" }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 950, color: brand.text }}>
                  Procurement Profile
                </Typography>
                <Typography sx={{ color: brand.subtext, mt: 0.5 }}>
                  Manage your organization details, invoices, RFQs, and security settings.
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap" }}>
                  <Chip icon={<ShieldCheck size={16} />} label="Verified buyer" />
                  <Chip icon={<Building2 size={16} />} label="Company: MAP Edu (Demo)" />
                </Stack>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={() => navigate("/B2BOrders")}
                  sx={{
                    borderRadius: 2.5,
                    fontWeight: 900,
                    textTransform: "none",
                    color: "white",
                    background: `linear-gradient(135deg, ${brand.accent} 0%, ${brand.accent2} 100%)`,
                  }}
                >
                  View orders
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/B2BRFQ")}
                  sx={{ borderRadius: 2.5, fontWeight: 900, textTransform: "none", borderColor: brand.stroke, color: brand.text }}
                >
                  Create RFQ
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Box>

        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 4, border: `1px solid ${brand.stroke}` }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "rgba(14,165,233,0.10)",
                    }}
                  >
                    <Building2 size={18} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 950, color: brand.text }}>Organization</Typography>
                    <Typography variant="body2" sx={{ color: brand.subtext }}>
                      Primary billing & delivery profile
                    </Typography>
                  </Box>
                </Stack>
                <Divider sx={{ my: 1.5 }} />
                <Stack spacing={0.75}>
                  <Typography variant="body2" sx={{ color: brand.subtext }}>
                    Legal name: MAP Edu Services
                  </Typography>
                  <Typography variant="body2" sx={{ color: brand.subtext }}>
                    GSTIN: 29ABCDE1234F1Z5
                  </Typography>
                  <Typography variant="body2" sx={{ color: brand.subtext }}>
                    City: Hyderabad • India
                  </Typography>
                </Stack>
                <Button
                  variant="text"
                  onClick={() => navigate("/B2BCheckout")}
                  sx={{ mt: 1.5, p: 0, textTransform: "none", fontWeight: 900 }}
                >
                  Update addresses
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 4, border: `1px solid ${brand.stroke}` }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "rgba(34,197,94,0.10)",
                    }}
                  >
                    <Receipt size={18} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 950, color: brand.text }}>Billing</Typography>
                    <Typography variant="body2" sx={{ color: brand.subtext }}>
                      Invoices & payment preferences
                    </Typography>
                  </Box>
                </Stack>
                <Divider sx={{ my: 1.5 }} />
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ color: brand.subtext }}>
                      Default payment
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 900, color: brand.text }}>
                      Net-30
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ color: brand.subtext }}>
                      Invoice format
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 900, color: brand.text }}>
                      GST
                    </Typography>
                  </Stack>
                </Stack>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/B2BOrders")}
                  sx={{
                    mt: 2,
                    borderRadius: 2.5,
                    textTransform: "none",
                    fontWeight: 900,
                    borderColor: brand.stroke,
                    color: brand.text,
                  }}
                  startIcon={<FileText size={16} />}
                >
                  Download invoices
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 4, border: `1px solid ${brand.stroke}` }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "rgba(2,6,23,0.06)",
                    }}
                  >
                    <Settings size={18} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 950, color: brand.text }}>Preferences</Typography>
                    <Typography variant="body2" sx={{ color: brand.subtext }}>
                      Notifications & approvals
                    </Typography>
                  </Box>
                </Stack>
                <Divider sx={{ my: 1.5 }} />
                <Stack spacing={0.75}>
                  <Typography variant="body2" sx={{ color: brand.subtext }}>
                    Email alerts: Enabled
                  </Typography>
                  <Typography variant="body2" sx={{ color: brand.subtext }}>
                    PO approval: Required
                  </Typography>
                  <Typography variant="body2" sx={{ color: brand.subtext }}>
                    Preferred vendors: 3
                  </Typography>
                </Stack>
                <Button
                  variant="text"
                  onClick={() => navigate("/B2BContracts")}
                  sx={{ mt: 1.5, p: 0, textTransform: "none", fontWeight: 900 }}
                  startIcon={<FileText size={16} />}
                >
                  View contracts
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={7}>
            <Card sx={{ borderRadius: 4, border: `1px solid ${brand.stroke}` }}>
              <CardContent>
                <Typography sx={{ fontWeight: 950, color: brand.text }}>Recent activity</Typography>
                <Typography variant="body2" sx={{ color: brand.subtext, mb: 2 }}>
                  Latest orders, RFQs, and invoice events.
                </Typography>
                <Stack spacing={1.25}>
                  {[
                    { title: "RFQ created", desc: "Packaging Tape (Case) • Qty 240 • Delivery: HYD", tag: "RFQ" },
                    { title: "Invoice generated", desc: "INV-2026-0412 • ₹18,400 • Net-30", tag: "Invoice" },
                    { title: "Order delivered", desc: "ORD-1182 • Safety Helmet (Bulk Pack)", tag: "Delivered" },
                  ].map((x) => (
                    <Box
                      key={x.title + x.desc}
                      sx={{
                        p: 1.5,
                        borderRadius: 3,
                        border: `1px solid ${brand.stroke}`,
                        bgcolor: "white",
                      }}
                    >
                      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 900, color: brand.text }} noWrap>
                            {x.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: brand.subtext }} noWrap>
                            {x.desc}
                          </Typography>
                        </Box>
                        <Chip size="small" label={x.tag} />
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card sx={{ borderRadius: 4, border: `1px solid ${brand.stroke}` }}>
              <CardContent>
                <Typography sx={{ fontWeight: 950, color: brand.text }}>Quick links</Typography>
                <Typography variant="body2" sx={{ color: brand.subtext, mb: 2 }}>
                  Jump to common actions.
                </Typography>
                <Stack spacing={1}>
                  <Button
                    variant="contained"
                    onClick={() => navigate("/B2BShop")}
                    sx={{
                      borderRadius: 3,
                      textTransform: "none",
                      fontWeight: 950,
                      color: "white",
                      background: `linear-gradient(135deg, ${brand.accent} 0%, ${brand.accent2} 100%)`,
                      justifyContent: "space-between",
                    }}
                    endIcon={<Receipt size={16} />}
                  >
                    Browse catalog
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate("/B2BCart")}
                    sx={{
                      borderRadius: 3,
                      textTransform: "none",
                      fontWeight: 950,
                      borderColor: brand.stroke,
                      color: brand.text,
                      justifyContent: "space-between",
                    }}
                    endIcon={<Receipt size={16} />}
                  >
                    Open cart
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate("/B2BOrders")}
                    sx={{
                      borderRadius: 3,
                      textTransform: "none",
                      fontWeight: 950,
                      borderColor: brand.stroke,
                      color: brand.text,
                      justifyContent: "space-between",
                    }}
                    endIcon={<FileText size={16} />}
                  >
                    Orders & invoices
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default B2BUserDashboard;

